import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import React from 'react';
import TagManager from 'react-gtm-module';
import { LoadingStatusValues } from '@/utils/constants';
import useIsRevision from '../useIsRevision';

type DataLayerSearchEventsProps = 'page_nav_click' | 'sort_search_result';
const EventsMap = new Map<DataLayerSearchEventsProps, Record<string, string>>([
  ['page_nav_click', { event: 'trackEvent-pageNavClick', event_name: 'page_nav_click' }],
  [
    'sort_search_result',
    { event: 'trackEvent-sortSearchResult', event_name: 'sort_search_results' },
  ],
]);

const useDataLayerSearchEvents = (eventType: DataLayerSearchEventsProps = 'sort_search_result') => {
  const [isDataLayerActive, setIsDataLayerActive] = React.useState(false);
  const { queryParams, status, pagination } = React.useContext(ElasticComponentContext);
  const isRevision = useIsRevision();

  React.useEffect(() => {
    if (status !== LoadingStatusValues.loading) return;
    if (!isDataLayerActive) return;

    const filters = queryParams?.filters;
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);
    const eventObject = EventsMap.get(eventType);

    const dataLayer = {
      ...eventObject,
      ...(!!queryParams?.q?.trim() && { search_term: queryParams?.q?.trim() }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      ...(!!searchFilters && { search_filters: searchFilters }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...(!!filters.is_patner_only && { is_partner_only_filter: filters.is_patner_only[0] }),
      ...searchFiltersObject,
      ...(eventType === 'sort_search_result' && {
        type: queryParams?.sortType
          ? `${queryParams?.sortBy}-${queryParams?.sortType}`
          : queryParams?.sortBy,
      }),
    };
    TagManager.dataLayer({ dataLayer });

    setIsDataLayerActive(false);
  }, [
    eventType,
    isDataLayerActive,
    isRevision,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams.category_type,
    queryParams?.filters,
    queryParams.page,
    queryParams?.q,
    queryParams?.sortBy,
    queryParams?.sortType,
    status,
  ]);

  const triggerSearchEventDataLayer = React.useCallback(() => {
    setIsDataLayerActive(true);
  }, []);

  return { triggerSearchEventDataLayer };
};

export default useDataLayerSearchEvents;
